import Vue from "vue";
import axios from "axios";
import VueAxios from "vue-axios";
import router from "../main";
import VueCookies from "vue-cookies";

// Enhanced dual authentication interceptors
let isInterceptorsConfigured = false;

const configureInterceptors = async () => {
  if (isInterceptorsConfigured) return;

  // Import store and auth service lazily to avoid circular dependencies
  const [{ default: store }, { default: authService }] = await Promise.all([
    import("../store"),
    import("@/services/auth/authService"),
  ]);

  // Request interceptor - add auth token
  axios.interceptors.request.use(
    async (config) => {
      try {
        const token = await authService.getToken();
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }

        // Log API calls in development
        if (process.env.NODE_ENV !== "production") {
          const authMethod = authService.getCurrentAuthMethod();
          console.log(
            `🔗 API Request: ${config.method?.toUpperCase()} ${config.url}`,
          );
          console.log("🎯 Base URL:", config.baseURL);
          console.log("🔑 Auth Method:", authMethod || "None");
          console.log(
            "🔑 Token Type:",
            authMethod === "entraId"
              ? "Backend JWT"
              : authMethod === "traditional"
                ? "Backend JWT"
                : "None",
          );
          console.log(
            "🔑 Token:",
            token ? `Bearer ${token.substring(0, 20)}...` : "None",
          );
        }

        return config;
      } catch (error) {
        console.error("Request interceptor error:", error);
        return config;
      }
    },
    (error) => {
      console.error("Request interceptor error:", error);
      return Promise.reject(error);
    },
  );

  // Response interceptor - handle auth failures with token refresh
  axios.interceptors.response.use(
    (response) => {
      // Log successful responses in development
      if (process.env.NODE_ENV !== "production") {
        console.log(
          `✅ API Response: ${response.status} ${response.config.method?.toUpperCase()} ${response.config.url}`,
        );
      }
      return response;
    },
    async (error) => {
      const originalRequest = error.config;

      // Skip auto-logout for login/authentication endpoints
      const isAuthEndpoint =
        originalRequest.url?.includes("/auth/login") ||
        originalRequest.url?.includes("/auth/entra-id/verify") ||
        originalRequest.url?.includes("/auth/user/exists");

      // Handle 401 errors - token is invalid or expired
      if (error.response?.status === 401 && !isAuthEndpoint) {
        const authMethod = store.getters.authMethod;

        console.warn("🚫 401 Unauthorized - Token invalid or expired");
        console.log("🔐 Auth method:", authMethod);

        // Only show session expired and logout if user was previously authenticated
        // Check if this is a legitimate session expiration (user was logged in)
        const wasAuthenticated =
          authMethod &&
          (authMethod === "entraId" || authMethod === "traditional");

        if (!wasAuthenticated) {
          // User was not authenticated, don't show session expired message
          console.log(
            "ℹ️ 401 received but user was not authenticated, skipping logout",
          );
          return Promise.reject(error);
        }

        // Initialize retry count
        if (!originalRequest._retryCount) {
          originalRequest._retryCount = 0;
        }

        // For Entra ID: Attempt backend token refresh before forcing logout
        if (authMethod === "entraId" && originalRequest._retryCount < 2) {
          originalRequest._retryCount++;

          console.log(
            `🔄 Attempt ${originalRequest._retryCount}: Trying to refresh backend JWT token before logout...`,
          );

          try {
            // Import authService and attempt token refresh
            const { default: authService } = await import(
              "@/services/auth/authService"
            );
            const refreshResult = await authService.refreshBackendToken();

            if (refreshResult.success) {
              console.log(
                "✅ Backend token refreshed successfully, retrying original request",
              );

              // Update the Authorization header with new token
              originalRequest.headers.Authorization = `Bearer ${refreshResult.token}`;

              // Retry the original request with new token
              return axios(originalRequest);
            } else {
              console.error(
                "❌ Backend token refresh failed:",
                refreshResult.error,
              );

              // Check if we should retry one more time
              if (originalRequest._retryCount < 2) {
                console.log("⚠️ Will retry one more time...");
                // Reject to trigger another 401 handling cycle
                return Promise.reject(error);
              }

              // Give up after 2 attempts
              console.error("🔴 Refresh attempts exhausted, forcing logout");
            }
          } catch (refreshError) {
            console.error("❌ Exception during token refresh:", refreshError);

            // Check if we should retry
            if (originalRequest._retryCount < 2) {
              console.log("⚠️ Will retry one more time...");
              return Promise.reject(error);
            }

            console.error("🔴 Refresh attempts exhausted, forcing logout");
          }

          // If we get here, refresh failed after retries
          console.warn(
            "🔴 Backend token refresh failed, clearing MSAL cache and forcing logout",
          );

          // Clear MSAL cache to prevent authentication loop
          try {
            const { default: entraIdAuth } = await import(
              "@/services/auth/entraIdAuth"
            );
            await entraIdAuth.clearAuthState();
            console.log("✅ MSAL cache cleared successfully");
          } catch (msalError) {
            console.error("Failed to clear MSAL cache:", msalError);
          }
        } else if (authMethod === "traditional") {
          console.warn("🔴 Traditional auth token invalid - forcing logout");
        } else if (authMethod === "entraId") {
          console.warn("🔴 Retry limit reached for Entra ID - forcing logout");
        }

        // Force logout the user
        console.warn("🚫 Forcing user logout due to invalid token");

        // Clear auth-related cookies
        VueCookies.remove("transaction-list-filter");
        VueCookies.remove("remitter-list-filter");
        VueCookies.remove("receiver-list-filter");
        VueCookies.remove("receiver-show-deleted");

        // Use Vuex store to handle unified logout
        try {
          const { UNIFIED_LOGOUT } = await import("../store/actions.type");
          await store.dispatch(UNIFIED_LOGOUT);
        } catch (logoutError) {
          console.error("Logout error:", logoutError);
        }

        // Show notification to user ONLY if they were previously authenticated
        try {
          // Dynamically import Vue to access $toast
          const Vue = (await import("vue")).default;
          if (Vue.prototype.$toast) {
            Vue.prototype.$toast.warning(
              "Session expired. Please login again.",
              {
                position: "top-right",
                duration: 5000,
              },
            );
          }
        } catch (toastError) {
          console.error("Toast notification error:", toastError);
        }

        // Redirect to login page after a brief delay to show the toast
        setTimeout(() => {
          router.push("/page-login");
        }, 100);

        // Reject the promise so calling code knows the request failed
        return Promise.reject(error);
      }

      // For auth endpoints, let the error propagate to the caller
      if (isAuthEndpoint) {
        console.log(
          "🔒 Authentication endpoint error, letting caller handle it",
        );
      }

      // Handle 403 errors - insufficient permissions
      if (error.response?.status === 403) {
        console.error("🚫 Access forbidden - insufficient permissions");
        const errorData = error.response.data;

        if (errorData?.missing) {
          console.error(`Missing required scope/role: ${errorData.missing}`);
          // You could show a user-friendly message here
          error.message = `You don't have permission to access this resource. Missing: ${errorData.missing}`;
        }
      }

      // Log errors in development
      if (process.env.NODE_ENV !== "production") {
        console.error(
          `❌ API Error: ${error.response?.status || "Network Error"} ${error.config?.method?.toUpperCase()} ${error.config?.url}`,
        );
        console.error("Error details:", error.response?.data);
      }

      return Promise.reject(error);
    },
  );

  isInterceptorsConfigured = true;
};

// Configure interceptors immediately
configureInterceptors();

const ApiService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = process.env.VUE_APP_PYTHON_URL;
  },

  setHeaderVerifyEmail() {
    // Deprecated: Headers are now set via interceptors
    console.warn(
      "setHeaderVerifyEmail is deprecated. Headers are automatically set via interceptors.",
    );
  },

  setHeader() {
    // Deprecated: Headers are now set via interceptors
    console.warn(
      "setHeader is deprecated. Headers are automatically set via interceptors.",
    );
  },

  query(resource, params) {
    return Vue.axios.get(resource, params).catch((error) => {
      throw new Error(`[RWV] ApiService ${error}`);
    });
  },
  get(resource, slug = "") {
    var url = `${resource}`;
    if (slug) {
      url = `${resource}/${slug}`;
    }
    const fullUrl = `${Vue.axios.defaults.baseURL}/${url}`;
    console.log("🌐 API Call: GET", url);
    console.log("🔗 Full URL:", fullUrl);
    console.log("🏢 Base URL:", Vue.axios.defaults.baseURL);
    return Vue.axios
      .get(`${url}`)
      .then((response) => {
        console.log("✅ API Response Success:", {
          status: response.status,
          statusText: response.statusText,
          url: response.config.url,
          dataLength: Array.isArray(response.data)
            ? response.data.length
            : response.data?.details
              ? response.data.details.length
              : "N/A",
        });
        return response;
      })
      .catch((error) => {
        console.error("❌ API Error:", {
          message: error.message,
          status: error.response?.status,
          statusText: error.response?.statusText,
          url: error.config?.url,
          data: error.response?.data,
        });
        throw new Error(`[RWV] ApiService ${error}`);
      });
  },

  post(resource, params) {
    return Vue.axios.post(`${resource}`, params);
  },

  update(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },

  put(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },

  patch(resource, params) {
    return Vue.axios.patch(`${resource}`, params);
  },

  delete(resource) {
    return Vue.axios.delete(resource).catch((error) => {
      throw new Error(`[RWV] ApiService ${error}`);
    });
  },
};

export default ApiService;

export const customerService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = process.env.VUE_APP_STANDAPP_URL;
  },
  setHeader() {
    Vue.axios.defaults.headers.common["Authorization"] = `Basic ${btoa(
      `${process.env.VUE_APP_STANDAPP_URL_USERNAME}:${process.env.VUE_APP_STANDAPP_URL_PASSWORD}`,
    )}`;
  },
  get(resource, slug) {
    return ApiService.get(`${resource}`, slug);
  },
  create(resource, params) {
    return Vue.axios.post(`${resource}`, params);
    //ApiService.post(`${resource}`, params);
  },
  patch(resource, params) {
    return ApiService.patch(`${resource}`, params);
  },
  put(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },
  post(resource, params) {
    return ApiService.post(`${resource}`, params);
  },
  destroy(resource) {
    return ApiService.delete(`${resource}`);
  },
};

export const ArchBaseService = {
  init() {
    Vue.use(VueAxios, axios);
    axios.defaults.baseURL = process.env.VUE_APP_ARCH_BASE_URL;
  },
  setHeader() {
    // Deprecated: Headers are now set via interceptors
    console.warn(
      "ArchBaseService.setHeader is deprecated. Headers are automatically set via interceptors.",
    );
  },
  get(resource, params = {}) {
    var url = `${resource}`;

    // If params is a string, treat it as a slug
    if (typeof params === "string" && params) {
      url = `${resource}/${params}`;
      return axios
        .get(`${url}`)
        .then((response) => {
          return response;
        })
        .catch((error) => {
          throw error;
        });
    }

    // If params is an object, treat it as query parameters
    return axios
      .get(`${url}`, { params })
      .then((response) => {
        return response;
      })
      .catch((error) => {
        throw error;
      });
  },
  create(resource, params) {
    return axios.post(`${resource}`, params);
  },
  patch(resource, params) {
    return axios.patch(`${resource}`, params);
  },
  put(resource, params) {
    return axios.put(`${resource}`, params);
  },
  post(resource, params) {
    return axios.post(`${resource}`, params);
  },
  destroy(resource) {
    return axios.delete(`${resource}`);
  },
};
export const ReceiverService = {
  init() {
    Vue.use(VueAxios, axios);
    axios.defaults.baseURL = process.env.VUE_APP_ARCH_BASE_URL;
  },
  setHeader() {
    // Deprecated: Headers are now set via interceptors
    console.warn(
      "ReceiverService.setHeader is deprecated. Headers are automatically set via interceptors.",
    );
  },
  get(resource, slug) {
    return axios.get(`${resource}`, slug);
  },
  create(resource, params) {
    return axios.post(`${resource}`, params);
  },
  patch(resource, params) {
    return axios.patch(`${resource}`, params);
  },
  put(resource, params) {
    return axios.put(`${resource}`, params);
  },
  post(resource, params) {
    return axios.post(`${resource}`, params);
  },
  destroy(resource) {
    return axios.delete(`${resource}`);
  },
};

export const BaseService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = process.env.VUE_APP_NODE_URL;
  },
  setHeader() {
    // Deprecated: Headers are now set via interceptors
    console.warn(
      "BaseService.setHeader is deprecated. Headers are automatically set via interceptors.",
    );
  },
  get(resource, slug) {
    return ApiService.get(`${resource}`, slug);
  },
  create(resource, params) {
    return Vue.axios.post(`${resource}`, params);
    //ApiService.post(`${resource}`, params);
  },
  patch(resource, params) {
    return ApiService.patch(`${resource}`, params);
  },
  post(resource, params) {
    return ApiService.post(`${resource}`, params);
  },
  destroy(resource) {
    return ApiService.delete(`${resource}`);
  },
};
export const partnerService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = process.env.VUE_APP_PYTHON_URL;
  },
  setHeader() {
    // Deprecated: Headers are now set via interceptors
    console.warn(
      "partnerService.setHeader is deprecated. Headers are automatically set via interceptors.",
    );
  },
  get(resource, slug) {
    return ApiService.get(`${resource}`, slug);
  },
  create(resource, params) {
    return Vue.axios.post(`${resource}`, params);
    //ApiService.post(`${resource}`, params);
  },
  patch(resource, params) {
    return ApiService.patch(`${resource}`, params);
  },
  put(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },
  post(resource, params) {
    return ApiService.post(`${resource}`, params);
  },
  destroy(resource) {
    return ApiService.delete(`${resource}`);
  },
};
export const transactionService = {
  init() {
    Vue.use(VueAxios, axios);
    axios.defaults.baseURL = process.env.VUE_APP_ARCH_BASE_URL;
  },
  setHeader() {
    // Deprecated: Headers are now set via interceptors
    console.warn(
      "transactionService.setHeader is deprecated. Headers are automatically set via interceptors.",
    );
  },
  get(resource, slug) {
    const url = slug ? `${resource}/${slug}` : resource;
    return Vue.axios.get(url, {
      headers: {
        "Cache-Control": "no-cache, no-store, must-revalidate",
        Pragma: "no-cache",
        Expires: "0",
      },
    });
  },
  create(resource, params) {
    return axios.post(`${resource}`, params);
  },
  patch(resource, params) {
    return axios.patch(`${resource}`, params);
  },
  put(resource, params) {
    return axios.put(`${resource}`, params);
  },
  post(resource, params) {
    return axios.post(`${resource}`, params);
  },
  destroy(resource) {
    return axios.delete(`${resource}`);
  },
};

export const StandappService = {
  init() {
    Vue.use(VueAxios, axios);
    Vue.axios.defaults.baseURL = process.env.VUE_APP_STANDAPP_URL;
  },
  setHeader() {
    Vue.axios.defaults.headers.common["Authorization"] = `Basic ${btoa(
      `${process.env.VUE_APP_STANDAPP_URL_USERNAME}:${process.env.VUE_APP_STANDAPP_URL_PASSWORD}`,
    )}`;
  },
  get(resource, slug) {
    return ApiService.get(`${resource}`, slug);
  },
  create(resource, params) {
    return Vue.axios.post(`${resource}`, params);
    //ApiService.post(`${resource}`, params);
  },
  patch(resource, params) {
    return ApiService.patch(`${resource}`, params);
  },
  put(resource, params) {
    return Vue.axios.put(`${resource}`, params);
  },
  post(resource, params) {
    return ApiService.post(`${resource}`, params);
  },
  destroy(resource) {
    return ApiService.delete(`${resource}`);
  },
};

export const BusinessService = {
  init() {
    Vue.use(VueAxios, axios);
    axios.defaults.baseURL = process.env.VUE_APP_ARCH_BASE_URL;
  },
  setHeader() {
    // Deprecated: Headers are now set via interceptors
    console.warn(
      "BusinessService.setHeader is deprecated. Headers are automatically set via interceptors.",
    );
  },
  get(resource, slug) {
    return axios.get(`${resource}`, slug);
  },
  create(resource, params) {
    return axios.post(`${resource}`, params);
  },
  patch(resource, params) {
    return axios.patch(`${resource}`, params);
  },
  put(resource, params) {
    return axios.put(`${resource}`, params);
  },
  post(resource, params) {
    return axios.post(`${resource}`, params);
  },
  destroy(resource) {
    return axios.delete(`${resource}`);
  },
};

export const RoleMenuService = {
  init() {
    Vue.use(VueAxios, axios);
    axios.defaults.baseURL = process.env.VUE_APP_ARCH_BASE_URL;
  },
  setHeader() {
    // Deprecated: Headers are now set via interceptors
    console.warn(
      "RoleMenuService.setHeader is deprecated. Headers are automatically set via interceptors.",
    );
  },
  get(resource, slug) {
    return axios.get(`${resource}`, slug);
  },
  create(resource, params) {
    return axios.post(`${resource}`, params);
  },
  patch(resource, params) {
    return axios.patch(`${resource}`, params);
  },
  put(resource, params) {
    return axios.put(`${resource}`, params);
  },
  post(resource, params) {
    return axios.post(`${resource}`, params);
  },
  destroy(resource) {
    return axios.delete(`${resource}`);
  },
};

export const BinService = {
  init() {
    Vue.use(VueAxios, axios);
    axios.defaults.baseURL = process.env.VUE_APP_ARCH_BASE_URL;
  },
  setHeader() {
    // Deprecated: Headers are now set via interceptors
    console.warn(
      "BinService.setHeader is deprecated. Headers are automatically set via interceptors.",
    );
  },
  get(resource, slug) {
    return axios.get(`${resource}`, slug);
  },
  create(resource, params) {
    return axios.post(`${resource}`, params);
  },
  patch(resource, params) {
    return axios.patch(`${resource}`, params);
  },
  put(resource, params) {
    return axios.put(`${resource}`, params);
  },
  post(resource, params) {
    return axios.post(`${resource}`, params);
  },
  destroy(resource) {
    return axios.delete(`${resource}`);
  },
};
